import React, { useState } from 'react';

import { Helmet } from 'react-helmet-async';
import { Route, Routes, useNavigate, Navigate } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import SingleCardTemplate from '@/components/layouts/SingleCardTemplate';
import SingleColumnTemplate from '@/components/layouts/SingleColumnTemplate';
import Jumbotron from '@/components/ui/box/Jumbotron';
import { pageTitles } from '@/constants/pageTitles';
import { type OrganizationMembershipFormValues } from '@/interfaces/organizationMembership';
import { type Student } from '@/interfaces/student';

import CSSModule from './OrganizationMembershipCreatePage.module.scss';
import OrganizationMembershipRegistrationCompleteForm from './OrganizationMembershipRegistrationCompleteForm';
import OrganizationMembershipRegistrationConfirmForm from './OrganizationMembershipRegistrationConfirmForm';
import OrganizationMembershipRegistrationCreateForm from './OrganizationMembershipRegistrationCreateForm';

const RegistrationCreateForm: React.FC<{
  formValues: OrganizationMembershipFormValues;
  onSubmitForm: (
    formValues: OrganizationMembershipFormValues,
    studentInfo: Student,
  ) => void;
  pageTitle: string;
}> = ({ formValues, onSubmitForm, pageTitle }) => (
  <SingleCardTemplate title={pageTitle}>
    <Helmet>
      <title>{pageTitle}</title>
    </Helmet>
    <OrganizationMembershipRegistrationCreateForm
      formValues={formValues}
      onSubmitForm={onSubmitForm}
    />
  </SingleCardTemplate>
);

const RegistrationConfirmForm: React.FC<{
  studentInfo: Student;
  formValues: OrganizationMembershipFormValues;
  onGoBack: () => void;
  onComplete: () => void;
}> = ({ studentInfo, formValues, onGoBack, onComplete }) => (
  <SingleCardTemplate title={pageTitles.mypageOrganizationMembershipsConfirm}>
    <Helmet>
      <title>{pageTitles.mypageOrganizationMembershipsConfirm}</title>
    </Helmet>
    <OrganizationMembershipRegistrationConfirmForm
      studentInfo={studentInfo}
      formValues={formValues}
      onGoBack={onGoBack}
      onComplete={onComplete}
    />
  </SingleCardTemplate>
);

const RegistrationCompleteForm: React.FC = () => (
  <SingleColumnTemplate>
    <div className={CSSModule.OrganizationMembershipCreatePage__Jumbotron}>
      <Jumbotron>
        <OrganizationMembershipRegistrationCompleteForm />
      </Jumbotron>
    </div>
  </SingleColumnTemplate>
);

const OrganizationMembershipCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [formValues, setFormValues] =
    useState<OrganizationMembershipFormValues>({
      code: '',
      first_name_kana: '',
      last_name_kana: '',
    });
  const [studentInfo, setStudentInfo] = useState<Student | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  const pageTitle = pageTitles.mypageOrganizationMembershipsNewV2;

  const onSubmitForm = (
    formValues: OrganizationMembershipFormValues,
    studentInfo: Student,
  ) => {
    setFormValues(formValues);
    setStudentInfo(studentInfo);
    navigate('/mypage/organization_memberships/new/confirm');
  };

  const onComplete = () => {
    setIsComplete(true);
    setFormValues({ code: '', first_name_kana: '', last_name_kana: '' });
    setStudentInfo(null);
    navigate('/mypage/organization_memberships/new/complete');
  };

  const onGoBack = () => {
    setFormValues({ code: '', first_name_kana: '', last_name_kana: '' });
    setStudentInfo(null);
    navigate('/mypage/organization_memberships/new');
  };

  // より厳密な確認条件チェック
  const canConfirm =
    studentInfo &&
    formValues.code &&
    formValues.code.trim().length > 0 &&
    studentInfo.id &&
    studentInfo.name &&
    studentInfo.organization_name;

  return (
    <Routes>
      <Route
        path="/"
        element={
          <RegistrationCreateForm
            formValues={formValues}
            onSubmitForm={onSubmitForm}
            pageTitle={pageTitle}
          />
        }
      />
      <Route
        path="/confirm"
        element={
          canConfirm ? (
            <RegistrationConfirmForm
              formValues={formValues}
              studentInfo={studentInfo}
              onGoBack={onGoBack}
              onComplete={onComplete}
            />
          ) : (
            <Navigate to="/mypage/organization_memberships/new" replace />
          )
        }
      />
      <Route
        path="/complete"
        element={
          isComplete ? (
            <RegistrationCompleteForm />
          ) : (
            <Navigate to="/mypage/organization_memberships/new" replace />
          )
        }
      />
      <Route path="*" element={<ErrorPage type="not_found" />} />
    </Routes>
  );
};

export default OrganizationMembershipCreatePage;
